import { REACTION_MAX_AGE_MS } from "@desfecha/contracts";
import type { MessageDto } from "../api/types";
import type { Message, MessageReaction } from "../data/demo";

export const SELF_USER_ID = "self";
/** WhatsApp's quick reactions, in its order; + opens the whole catalog. */
export const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];
/** The contact's side of a conversation; ours is SELF_USER_ID, whoever of the team reacted. */
export const CONTACT_USER_ID = "contact";

/** Old string lists represented repeated local choices: the last one wins. */
export function normalizeReactions(input: unknown): MessageReaction[] {
  const byUser = new Map<string, MessageReaction>();
  if (!Array.isArray(input)) return [];
  for (const value of input) {
    const reaction =
      typeof value === "string"
        ? { userId: SELF_USER_ID, emoji: value }
        : value;
    if (
      reaction &&
      typeof reaction.userId === "string" &&
      typeof reaction.emoji === "string" &&
      reaction.emoji &&
      reaction.emoji !== "Vou participar"
    ) {
      byUser.set(reaction.userId, {
        userId: reaction.userId,
        emoji: reaction.emoji,
      });
    }
  }
  return [...byUser.values()];
}

export function ownReaction(message: Message, userId = SELF_USER_ID) {
  return normalizeReactions(message.reactions).find((r) => r.userId === userId)
    ?.emoji;
}

export function setReaction(
  message: Message,
  emoji: string,
  userId = SELF_USER_ID,
) {
  if (message.deleted || !emoji) return;
  const previous = ownReaction(message, userId);
  message.reactions = normalizeReactions(message.reactions).filter(
    (r) => r.userId !== userId,
  );
  if (previous !== emoji) message.reactions.push({ userId, emoji });
}

export function groupedReactions(message: Message) {
  const groups = new Map<
    string,
    { emoji: string; count: number; own: boolean }
  >();
  for (const reaction of normalizeReactions(message.reactions)) {
    const group = groups.get(reaction.emoji) || {
      emoji: reaction.emoji,
      count: 0,
      own: false,
    };
    group.count++;
    group.own ||= reaction.userId === SELF_USER_ID;
    groups.set(reaction.emoji, group);
  }
  return [...groups.values()];
}

/**
 * WhatsApp keeps one reaction per side on a message: the contact's and the business's (every
 * member speaks for the business). Reaction messages fold onto the message they react to, by its
 * WhatsApp id: the latest of each side wins, an empty emoji takes it away, and one WhatsApp
 * refused never reached the contact.
 */
export function foldReactions(
  messages: MessageDto[],
): Map<string, MessageReaction[]> {
  const latest = new Map<string, Map<string, string>>();
  for (const m of messages) {
    if (m.type !== "reaction" || m.status === "failed") continue;
    const r = m.content?.reaction as
      { emoji?: string; targetProviderMessageId?: string } | undefined;
    if (!r?.targetProviderMessageId) continue;
    const sides = latest.get(r.targetProviderMessageId) ?? new Map();
    sides.set(
      m.direction === "inbound" ? CONTACT_USER_ID : SELF_USER_ID,
      r.emoji ?? "",
    );
    latest.set(r.targetProviderMessageId, sides);
  }
  const folded = new Map<string, MessageReaction[]>();
  for (const [target, sides] of latest) {
    const list = [...sides]
      .filter(([, emoji]) => emoji)
      .map(([userId, emoji]) => ({ userId, emoji }));
    if (list.length) folded.set(target, list);
  }
  return folded;
}

/**
 * Whether WhatsApp takes our reaction on this message: one the contact sent, up to 30 days old
 * and not deleted. The 24-hour window is the conversation's, checked apart.
 */
export function canReactTo(m: MessageDto, now = Date.now()): boolean {
  return (
    m.direction === "inbound" &&
    Boolean(m.providerMessageId) &&
    m.type !== "reaction" &&
    m.type !== "system" &&
    m.status !== "deleted" &&
    m.content?.purged !== true &&
    now - new Date(m.createdAt).getTime() <= REACTION_MAX_AGE_MS
  );
}
